const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '')

const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveLocalMainSiteUrl() {
  if (typeof window === 'undefined') {
    return 'https://fastdo.tools'
  }

  const { hostname, protocol } = window.location

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4001`
  }

  return 'https://fastdo.tools'
}

const mainSiteUrl = stripTrailingSlash(viteEnv.VITE_MAIN_SITE_URL ?? resolveLocalMainSiteUrl())
const authApiBaseUrl = stripTrailingSlash(
  viteEnv.VITE_AUTH_API_BASE_URL ?? resolveLocalMainSiteUrl(),
)

function resolveApiBaseUrl() {
  if (authApiBaseUrl) return authApiBaseUrl
  if (mainSiteUrl) return mainSiteUrl
  return ''
}

function resolveEndpoint(explicitValue: string | undefined, fallbackPath: string): string {
  if (explicitValue && explicitValue.trim()) {
    return explicitValue.trim()
  }

  const apiBaseUrl = resolveApiBaseUrl()
  if (apiBaseUrl) {
    return `${apiBaseUrl}${fallbackPath}`
  }

  return fallbackPath
}

const AZURE_TTS_ENDPOINT = resolveEndpoint(
  viteEnv.VITE_SUBSITE_AZURE_TTS_ENDPOINT,
  '/api/subsite/tts/azure',
)

export const AZURE_TTS_VOICE_OPTIONS = [
  { value: 'zh-CN-XiaoxiaoNeural', label: '女声-温柔（晓晓）' },
  { value: 'zh-CN-XiaoyiNeural', label: '女声-甜美（晓伊）' },
  { value: 'zh-CN-XiaomoNeural', label: '女声-知性（晓墨）' },
  { value: 'zh-CN-XiaoqiuNeural', label: '女声-活力（晓秋）' },
  { value: 'zh-CN-XiaoruiNeural', label: '女声-成熟（晓睿）' },
  { value: 'zh-CN-XiaoshuangNeural', label: '女声-干练（晓双）' },
  { value: 'zh-CN-XiaoxuanNeural', label: '女声-沉静（晓萱）' },
  { value: 'zh-CN-XiaoyanNeural', label: '女声-客服（晓颜）' },
  { value: 'zh-CN-XiaozhenNeural', label: '女声-温婉（晓甄）' },
  { value: 'zh-CN-YunfengNeural', label: '男声-稳重（云枫）' },
  { value: 'zh-CN-YunhaoNeural', label: '男声-自然（云皓）' },
  { value: 'zh-CN-YunjianNeural', label: '男声-专业（云健）' },
  { value: 'zh-CN-YunxiNeural', label: '男声-磁性（云希）' },
  { value: 'zh-CN-YunyangNeural', label: '男声-新闻（云扬）' },
  { value: 'zh-CN-YunyeNeural', label: '男声-青年（云野）' },
  { value: 'zh-CN-XiaoxiaoMultilingualNeural', label: '女声-多语温柔（晓晓）' },
  { value: 'zh-CN-YunyiMultilingualNeural', label: '男声-多语沉稳（云逸）' },
  { value: 'zh-HK-HiuGaaiNeural', label: '粤语女声（晓佳）' },
  { value: 'zh-HK-HiuMaanNeural', label: '粤语女声（晓曼）' },
  { value: 'zh-HK-WanLungNeural', label: '粤语男声（云龙）' },
  { value: 'zh-TW-HsiaoChenNeural', label: '台湾女声（晓臻）' },
  { value: 'zh-TW-HsiaoYuNeural', label: '台湾女声（晓雨）' },
  { value: 'zh-TW-YunJheNeural', label: '台湾男声（云哲）' },
  { value: 'en-US-AvaMultilingualNeural', label: '英文女声-多语（Ava）' },
  { value: 'en-US-AndrewMultilingualNeural', label: '英文男声-多语（Andrew）' },
  { value: 'en-US-EmmaMultilingualNeural', label: '英文女声-清晰（Emma）' },
  { value: 'en-US-BrianMultilingualNeural', label: '英文男声-稳重（Brian）' },
  { value: 'ja-JP-NanamiNeural', label: '日语女声（Nanami）' },
  { value: 'ja-JP-KeitaNeural', label: '日语男声（Keita）' },
  { value: 'ko-KR-SunHiNeural', label: '韩语女声（SunHi）' },
  { value: 'ko-KR-InJoonNeural', label: '韩语男声（InJoon）' },
] as const

export type AzureTtsVoice = (typeof AZURE_TTS_VOICE_OPTIONS)[number]['value']

function pickAzureVoiceOptions(voices: AzureTtsVoice[]) {
  return voices.map((voice) => getAzureTtsVoiceOption(voice))
}

export const AZURE_TTS_VOICE_GROUPS = [
  {
    label: '普通话女声',
    options: pickAzureVoiceOptions([
      'zh-CN-XiaoxiaoNeural',
      'zh-CN-XiaoyiNeural',
      'zh-CN-XiaomoNeural',
      'zh-CN-XiaoqiuNeural',
      'zh-CN-XiaoruiNeural',
      'zh-CN-XiaoshuangNeural',
      'zh-CN-XiaoxuanNeural',
      'zh-CN-XiaoyanNeural',
      'zh-CN-XiaozhenNeural',
    ]),
  },
  {
    label: '普通话男声',
    options: pickAzureVoiceOptions([
      'zh-CN-YunfengNeural',
      'zh-CN-YunhaoNeural',
      'zh-CN-YunjianNeural',
      'zh-CN-YunxiNeural',
      'zh-CN-YunyangNeural',
      'zh-CN-YunyeNeural',
    ]),
  },
  {
    label: '多语角色',
    options: pickAzureVoiceOptions([
      'zh-CN-XiaoxiaoMultilingualNeural',
      'zh-CN-YunyiMultilingualNeural',
      'en-US-AvaMultilingualNeural',
      'en-US-AndrewMultilingualNeural',
      'en-US-EmmaMultilingualNeural',
      'en-US-BrianMultilingualNeural',
    ]),
  },
  {
    label: '粤语与台湾中文',
    options: pickAzureVoiceOptions([
      'zh-HK-HiuGaaiNeural',
      'zh-HK-HiuMaanNeural',
      'zh-HK-WanLungNeural',
      'zh-TW-HsiaoChenNeural',
      'zh-TW-HsiaoYuNeural',
      'zh-TW-YunJheNeural',
    ]),
  },
  {
    label: '日语与韩语',
    options: pickAzureVoiceOptions([
      'ja-JP-NanamiNeural',
      'ja-JP-KeitaNeural',
      'ko-KR-SunHiNeural',
      'ko-KR-InJoonNeural',
    ]),
  },
] as const

function createOutputFileName(text: string, voice: AzureTtsVoice): string {
  const safeText =
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'speech'
  const safeVoice = voice.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-tts-${safeText}-${safeVoice}-azure-${timestamp}.mp3`
}

function speedToRatePercent(speed: number): number {
  const normalized = Number.isFinite(speed) ? speed : 1
  return Math.max(-50, Math.min(100, Math.round((normalized - 1) * 100)))
}

class AzureTtsService {
  isSupported(): boolean {
    return typeof fetch === 'function'
  }

  async generateSpeechFile({
    text,
    voice,
    speed,
    onProgress,
  }: {
    text: string
    voice: AzureTtsVoice
    speed: number
    onProgress?: (stage: string) => void
  }): Promise<{ blob: Blob; file: File; duration: number }> {
    const trimmedText = text.trim()
    if (!trimmedText) {
      throw new Error('请输入要合成的文本。')
    }

    onProgress?.('正在连接 Azure TTS...')

    const response = await fetch(AZURE_TTS_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: trimmedText,
        voice,
        rate: speedToRatePercent(speed),
      }),
    })

    if (!response.ok) {
      let message = 'Azure TTS 请求失败。'

      try {
        const payload = (await response.json()) as { message?: string; error?: string }
        message = payload.message || payload.error || message
      } catch {
        // ignore invalid JSON body
      }

      throw new Error(message)
    }

    onProgress?.('正在接收云端音频...')
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('Azure TTS 没有返回音频数据。')
    }

    const file = new File([blob], createOutputFileName(trimmedText, voice), {
      type: blob.type || 'audio/mp3',
      lastModified: Date.now(),
    })

    const duration = await this.readDuration(blob)
    return { blob, file, duration }
  }

  private async readDuration(blob: Blob): Promise<number> {
    if (typeof Audio === 'undefined') {
      return 0
    }

    const objectUrl = URL.createObjectURL(blob)

    try {
      const duration = await new Promise<number>((resolve) => {
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.src = objectUrl
        audio.onloadedmetadata = () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0)
        audio.onerror = () => resolve(0)
      })
      return duration
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }
}

export function getAzureTtsVoiceOption(voice: AzureTtsVoice): {
  value: AzureTtsVoice
  label: string
} {
  return (
    AZURE_TTS_VOICE_OPTIONS.find((option) => option.value === voice) ?? {
      value: voice,
      label: voice,
    }
  )
}

export const azureTtsService = new AzureTtsService()
